// Modal.js
import React, { useEffect, useRef } from 'react';
import { IoMdClose } from './icons';
import styles from '../css/modules/Modal.module.css';

/* Elementy, na które można ustawić fokus wewnątrz okna - potrzebne
   do pułapki na Tab. Ten sam zestaw co w HamburgerMenu.jsx, bo modal
   ma się zachowywać dokładnie tak samo. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = ({ children, isOpen, onClose, extraClass = '', ariaLabel = 'Okno dialogowe' }) => {
  const dialogRef = useRef(null);
  /* Element, który otworzył modal - po zamknięciu fokus musi na niego wrócić,
     inaczej użytkownik klawiatury ląduje na początku strony. */
  const openerRef = useRef(null);

  /* Blokada przewijania tła bez przesuwania układu.
     `overflow: hidden` na <body> chowa pionowy pasek przewijania, przez co
     na desktopie treść skoczyłaby w bok o jego szerokość - rekompensujemy to
     paddingiem. Na telefonach pasek jest nakładkowy, więc rekompensata = 0. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const { body, documentElement } = document;
    const gutter = window.innerWidth - documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;

    body.style.overflow = 'hidden';
    // pasek przewijania nigdy nie jest szerszy niż ~20 px; większa różnica
    // oznaczałaby poziome przepełnienie strony, którego nie wolno kompensować
    if (gutter > 0 && gutter <= 20) {
      const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${current + gutter}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [isOpen]);

  /* Escape zamyka, Tab krąży wewnątrz okna. Modal obsługuje logowanie
     i rejestrację, więc fokus nie może z niego uciec na tło. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)].filter(
        el => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  /* Fokus wchodzi do okna po otwarciu i wraca na element otwierający po zamknięciu.
     preventScroll jest konieczne: fokus w elemencie position: fixed
     kazałby przeglądarce przewinąć stronę na samą górę. */
  useEffect(() => {
    if (isOpen) {
      openerRef.current = document.activeElement;
      const target = dialogRef.current?.querySelector(FOCUSABLE);
      target?.focus({ preventScroll: true });
      return undefined;
    }
    const opener = openerRef.current;
    openerRef.current = null;
    opener?.focus?.({ preventScroll: true });
    return undefined;
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.modal} onClick={onClose}>
      <div
        ref={dialogRef}
        className={`${styles.modalContent} ${extraClass}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Zamknij okno"
        >
          <IoMdClose size={22} aria-hidden="true" focusable="false" />
        </button>
        <div className={styles.scrollableContent}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
